import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

import './consent.css';
import { CONSENT_COPY, CONSENT_FALLBACK_LANG, type ConsentCopy } from './translations';

/**
 * Cookie consent (DIT-402).
 *
 * Imperative on purpose: the library owns its own DOM outside the React tree, so
 * there is nothing for the prerender to bake and nothing to hydrate. That matters
 * here — every marketing route is prerendered by `vite-react-ssg`, and a React
 * banner would either ship a frozen "not yet asked" state into the static HTML or
 * mismatch on hydration. It also means one init covers both the marketing site
 * and the funnel, which are separate route trees with separate i18n systems.
 *
 * Nothing on the site currently sets a non-essential cookie, so the banner ships
 * with only the necessary category active. The point is that the gate exists
 * before the first analytics tag does, not that it has something to block today.
 */

export const CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing', 'preferences'] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/** Bump to re-ask everyone — e.g. when a category is added or its purpose changes. */
const REVISION = 1;

/**
 * Six months, the retention the Belgian DPA and CNIL both point at. Longer is
 * legal in places but this is a compliance product; the conservative number is
 * the one we can defend.
 */
const EXPIRES_AFTER_DAYS = 182;

/** Builds the library's nested translation shape from one flat copy entry. */
function translation(c: ConsentCopy) {
  return {
    consentModal: {
      title: c.title,
      description: c.description,
      acceptAllBtn: c.acceptAll,
      acceptNecessaryBtn: c.rejectAll,
      showPreferencesBtn: c.manage,
      // No close icon on the banner on purpose. The library renders one as soon
      // as `closeIconLabel` is set, and a bare X next to Accept/Reject is an
      // ambiguous third option — the visitor has to make an actual choice.
    },
    preferencesModal: {
      title: c.prefTitle,
      acceptAllBtn: c.acceptAll,
      acceptNecessaryBtn: c.rejectAll,
      savePreferencesBtn: c.save,
      closeIconLabel: c.close,
      sections: [
        { title: c.introTitle, description: c.introBody },
        {
          title: c.necessaryTitle,
          description: c.necessaryBody,
          linkedCategory: 'necessary',
          // The inventory is here rather than in the privacy policy on purpose:
          // that document is a CMS-published contract with no in-repo fallback,
          // so it is the client's to edit, not ours.
          //
          // It lists local storage alongside the cookies, which is why the first
          // column is "Name" and not "Cookie": ePrivacy art. 5(3) covers both
          // identically, and the description above enumerates what is kept, so
          // the table and that sentence have to agree — see `necessaryBody`.
          cookieTable: {
            headers: { name: c.colName, purpose: c.colPurpose, expiry: c.colExpiry },
            body: [
              { name: 'pref_locale', purpose: c.localeCookie, expiry: c.expiry12m },
              { name: 'di_consent', purpose: c.consentCookie, expiry: c.expiry6m },
              {
                name: 'doneit-subscription-v1',
                purpose: c.signupState,
                expiry: c.expiryStorage,
              },
            ],
          },
        },
        { title: c.analyticsTitle, description: c.analyticsBody, linkedCategory: 'analytics' },
        { title: c.marketingTitle, description: c.marketingBody, linkedCategory: 'marketing' },
        {
          title: c.preferencesTitle,
          description: c.preferencesBody,
          linkedCategory: 'preferences',
          cookieTable: {
            headers: { name: c.colName, purpose: c.colPurpose, expiry: c.colExpiry },
            body: [
              // DIT-336. Declared here rather than under "strictly necessary", where it
              // first landed only because the widget used to load before this banner was
              // answered — which left the visitor no way to refuse it. The chat now waits
              // for this category, so the declaration and the behaviour agree.
              { name: 'cw_conversation', purpose: c.supportChat, expiry: c.expiry12m },
            ],
          },
        },
      ],
    },
  };
}

const TRANSLATIONS = Object.fromEntries(
  Object.entries(CONSENT_COPY).map(([code, copy]) => [code, translation(copy)]),
);

/**
 * The locale is the first path segment, the same source of truth the rest of the
 * site uses. Read from the URL rather than from either i18n instance because this
 * runs before both of them.
 */
function langFromPath(): string {
  if (typeof window === 'undefined') return CONSENT_FALLBACK_LANG;
  const first = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && first in CONSENT_COPY ? first : CONSENT_FALLBACK_LANG;
}

/** Resolves once the banner is up. Null until `initConsent()` is called. */
let ready: Promise<unknown> | null = null;

/** Boots the banner. Safe to call more than once; only the first call runs. */
export function initConsent(): void {
  if (ready || typeof window === 'undefined') return;

  ready = CookieConsent.run({
    autoShow: true,
    // Opt-in: nothing in a non-necessary category runs until it is granted.
    mode: 'opt-in',
    revision: REVISION,
    cookie: {
      name: 'di_consent',
      expiresAfterDays: EXPIRES_AFTER_DAYS,
      sameSite: 'Lax',
      path: '/',
    },
    guiOptions: {
      // Bottom CENTRE, not bottom-left: the marketing shell already owns that
      // corner with the language-suggestion nudge (`bottom-4 left-4`), and both
      // appear on a first visit whenever the browser language differs from the
      // URL locale — which is most of our audience. Bottom-left buried it.
      consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {},
      marketing: {},
      preferences: {},
    },
    language: {
      default: langFromPath(),
      // The path prefix decides the language, so no sniffing of the browser.
      translations: TRANSLATIONS,
    },
    // A banner that fails to start must not take the page down with it, and an
    // unhandled rejection here would surface as a console error on every load.
  }).catch(() => {});
}

/**
 * Keeps the banner on the locale the visitor is actually looking at. Language is
 * fixed at init, but the switcher navigates client-side, so nothing would reload.
 */
export function setConsentLanguage(code: string): void {
  const next = code?.toLowerCase();
  if (!ready || !next || !(next in CONSENT_COPY)) return;
  // Queue behind init rather than firing straight away: `run()` is async, so a
  // layout effect can reach this before the banner exists and the call would be
  // dropped on the floor.
  void ready.then(() => CookieConsent.setLanguage(next)).catch(() => {});
}

/**
 * Whether a category may run. Use this before loading anything non-essential;
 * for plain script tags prefer the library's own `data-category` attribute so
 * the tag can never fire ahead of the check.
 *
 * It has no callers yet, and that is the state of the feature rather than dead
 * code: there is no analytics or marketing script on the site to gate. This is
 * the thing the first one has to go through.
 */
export function hasConsent(category: ConsentCategory): boolean {
  try {
    return CookieConsent.acceptedCategory(category);
  } catch {
    // Called before init, or init failed. Deny — the fail-safe answer for
    // consent is always "no".
    return false;
  }
}
